// components/confirmModal/ConfirmModal.jsx

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import * as bootstrap from 'bootstrap'; // Importação mais robusta

const ConfirmModal = ({
  id = 'confirmModal',
  title,
  message,
  onConfirm,
  onClose,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  show,
}) => {
  const modalRef = useRef(null);
  const modalInstanceRef = useRef(null);
  const confirmBtnRef = useRef(null); // Ref para foco automático
  const cancelBtnRef = useRef(null);

  // 1. Inicialização Simples
  useEffect(() => {
    if (modalRef.current) {
      modalInstanceRef.current = new bootstrap.Modal(modalRef.current, {
        backdrop: 'static',
        keyboard: false,
      });
    }
    // Pequena segurança: limpar ao desmontar
    return () => {
      if (modalInstanceRef.current) {
        modalInstanceRef.current.hide();
        modalInstanceRef.current.dispose();
      }
    };
  }, []);

  // 2. Controle de Exibição via props
  useEffect(() => {
    if (modalInstanceRef.current) {
      if (show) {
        modalInstanceRef.current.show();
        // Foca no botão de confirmar para agilizar
        setTimeout(() => confirmBtnRef.current?.focus(), 100);
      } else {
        modalInstanceRef.current.hide();
      }
    }
  }, [show]);

  const handleConfirm = () => {
    // Esconde PRIMEIRO para evitar que o texto mude no fundo enquanto fecha
    modalInstanceRef.current?.hide();
    onConfirm?.();
  };

  const handleClose = () => {
    modalInstanceRef.current?.hide();
    onClose?.();
  };

  // 3. ATALHOS DE TECLADO (F9 e ESC)
  useEffect(() => {
    let isReady = false;

    const timer = setTimeout(() => {
      isReady = true;
    }, 200);

    const handleKeyDown = (e) => {
      if (!show || !isReady) return;

      // F9 para Confirmar (Enter só confirma se o foco não estiver no botão cancelar)
      if (e.key === 'F9' || e.key === 'Enter') {
        if (e.key === 'Enter' && document.activeElement === cancelBtnRef.current) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        handleConfirm();
        return;
      }

      // ESC para Cancelar
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }
    };

    if (show) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [show, onConfirm, onClose]);

  // 4. ESTILIZAÇÃO MELHORADA
  // Portal pro <body>: chamadores que usam ConfirmModal dentro de tabelas
  // (ex: uma modal por linha em NfeRow.jsx) fariam esse <div> virar filho
  // direto de <tbody>/<tr>, o que é HTML invalido (validateDOMNesting) e
  // atrapalha o próprio Bootstrap (modal deveria ser sempre um elemento de
  // nivel de <body>). O portal resolve isso independente de onde a modal é
  // declarada no JSX.
  return createPortal(
    <div
      className="modal fade"
      id={id}
      tabIndex="-1"
      aria-hidden="true"
      ref={modalRef}
      style={{ zIndex: 1090 }} // Garante que fique acima de outras modais
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content shadow-lg border-0">
          {/* Cabeçalho Vermelho (Padrão de Perigo/Atenção) */}
          <div className="modal-header bg-danger text-white py-2">
            <h6 className="modal-title fw-bold">{title}</h6>
            <button type="button" className="btn-close btn-close-white" onClick={handleClose} />
          </div>

          {/* Corpo com fonte maior */}
          <div className="modal-body p-4">
            <div className="fw-bold mb-0 fs-5">{message}</div>
          </div>

          {/* Rodapé Cinza */}
          <div className="modal-footer bg-light py-1">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleClose}
              title="Pressione ESC"
            >
              {cancelText} (ESC)
            </button>
            <button
              ref={confirmBtnRef}
              type="button"
              className="btn btn-danger fw-bold px-4 btn-sm"
              onClick={handleConfirm}
              title="Pressione F9"
            >
              {confirmText} (F9)
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

ConfirmModal.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  onConfirm: PropTypes.func.isRequired,
  // Opcionais: telas que abrem esta modal via API imperativa do Bootstrap
  // (bootstrap.Modal.getOrCreateInstance(el).show(), disparado por id — ex:
  // CadProduto.js, Nfe.jsx) não precisam de show/onClose controlados por
  // estado do React; a modal já trata os dois como opcionais internamente.
  onClose: PropTypes.func,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  show: PropTypes.bool,
};

export default ConfirmModal;
